import type { ReactNode } from "react";
import { Link } from "react-router-dom";

/**
 * Разметка статей справки (E16) — намеренно узкое подмножество Markdown.
 *
 * ПОЧЕМУ СВОЙ РАЗБОР, А НЕ БИБЛИОТЕКА. Корпус пишем мы сами и целиком лежит в репозитории:
 * недоверенного текста здесь нет, а значит нет и задачи, ради которой берут полноценный
 * парсер с санитайзером. Взамен приносить в клиент зависимость с транзитивным деревом ради
 * пятнадцати статей — плохая сделка (тем же доводом в проекте посчитан контраст WCAG вручную).
 *
 * ЧТО ПОДДЕРЖИВАЕТСЯ: `##`/`###` заголовки, абзацы, списки `-` и `1.`, `**жирный**` и ссылки
 * `[текст](/адрес)` — только внутренние, начинающиеся со слэша. Всё остальное (таблицы,
 * картинки, код, внешние ссылки) в корпус не пускает тест-сторож: неподдержанная разметка
 * иначе просто вывелась бы читателю как есть — сырыми звёздочками и решётками.
 */

/**
 * Ссылка внутри статьи: только внутренний путь.
 *
 * `\/(?!\/)` — один слэш, а не два: адрес вида `//чужой-хост` браузер понимает как ВНЕШНИЙ
 * (протокол берётся от текущей страницы), и без этого условия опечатка в одном символе увела бы
 * читателя из системы, пройдя и разбор, и сторожа как «внутренняя ссылка».
 */
const LINK = /\[([^\]\n]+)\]\((\/(?!\/)[^)\s]*)\)/g;
/** Жирный фрагмент. */
const BOLD = /\*\*([^*\n]+)\*\*/g;

/** Разбор строки на жирный текст и ссылки. */
function inline(text: string, keyPrefix: string): ReactNode[] {
  const out: ReactNode[] = [];
  let cursor = 0;

  // Сначала ссылки: их подпись может содержать «жирный», и наоборот не бывает.
  LINK.lastIndex = 0;
  let match: RegExpExecArray | null;
  let last = 0;
  while ((match = LINK.exec(text)) !== null) {
    if (match.index > last) {
      out.push(...bold(text.slice(last, match.index), `${keyPrefix}-t${cursor}`));
      cursor += 1;
    }
    out.push(
      <Link key={`${keyPrefix}-l${cursor}`} to={match[2]!} className="text-accent underline">
        {/* Подпись ссылки тоже проходит через разбор жирного — иначе «[**текст**](/x)» вывелся
            бы читателю со звёздочками, а сторож пары «**…**» этого не заметил бы. */}
        <>{bold(match[1]!, `${keyPrefix}-lb${cursor}`)}</>
      </Link>,
    );
    cursor += 1;
    last = match.index + match[0].length;
  }
  if (last < text.length) out.push(...bold(text.slice(last), `${keyPrefix}-t${cursor}`));
  return out;
}

/** Разбор фрагмента без ссылок на обычный и жирный текст. */
function bold(text: string, keyPrefix: string): ReactNode[] {
  const out: ReactNode[] = [];
  BOLD.lastIndex = 0;
  let last = 0;
  let i = 0;
  let match: RegExpExecArray | null;
  while ((match = BOLD.exec(text)) !== null) {
    if (match.index > last) out.push(text.slice(last, match.index));
    out.push(
      <strong key={`${keyPrefix}-b${i++}`} className="font-bold">
        {match[1]}
      </strong>,
    );
    last = match.index + match[0].length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

const BULLET = /^-\s+(.*)$/;
const NUMBERED = /^\d+\.\s+(.*)$/;

/**
 * Тело статьи → элементы. Блоки разделяются пустой строкой; список — подряд идущие строки,
 * начинающиеся с `- ` либо с «N. ».
 */
export function renderHelpBody(body: string): ReactNode[] {
  const blocks: ReactNode[] = [];
  const lines = body.split(/\r?\n/);
  let i = 0;
  let key = 0;

  while (i < lines.length) {
    const line = lines[i]!;
    if (line.trim() === "") {
      i += 1;
      continue;
    }

    if (line.startsWith("### ")) {
      blocks.push(
        <h3 key={`h${key++}`} className="mt-5 text-[14px] font-bold text-ink">
          {inline(line.slice(4).trim(), `h${key}`)}
        </h3>,
      );
      i += 1;
      continue;
    }
    if (line.startsWith("## ")) {
      blocks.push(
        <h2 key={`h${key++}`} className="mt-6 text-[15px] font-bold text-ink">
          {inline(line.slice(3).trim(), `h${key}`)}
        </h2>,
      );
      i += 1;
      continue;
    }

    const listKind = BULLET.test(line) ? "ul" : NUMBERED.test(line) ? "ol" : null;
    if (listKind !== null) {
      const items: string[] = [];
      while (i < lines.length) {
        const current = lines[i]!;
        const matched = listKind === "ul" ? BULLET.exec(current) : NUMBERED.exec(current);
        if (matched !== null) {
          items.push(matched[1]!);
          i += 1;
          continue;
        }
        // ПРОДОЛЖЕНИЕ ПУНКТА — строка с отступом под уже начатым пунктом. Без этой ветки длинный
        // пункт, перенесённый на вторую строку (а в корпусе так набрано большинство), обрывался
        // на середине фразы: хвост выпадал наружу отдельным абзацем, а следующий пункт начинал
        // список заново — с «1.» вместо «2.».
        if (items.length > 0 && /^\s+\S/.test(current) && !isBlockStart(current.trim())) {
          items[items.length - 1] = `${items[items.length - 1]!} ${current.trim()}`;
          i += 1;
          continue;
        }
        break;
      }
      const cls =
        listKind === "ul"
          ? "mt-3 list-disc space-y-1.5 pl-5 text-[14px] leading-[1.6] text-ink"
          : "mt-3 list-decimal space-y-1.5 pl-5 text-[14px] leading-[1.6] text-ink";
      const children = items.map((item, index) => (
        <li key={index}>{inline(item, `li${key}-${index}`)}</li>
      ));
      blocks.push(
        listKind === "ul" ? (
          <ul key={`l${key++}`} className={cls}>
            {children}
          </ul>
        ) : (
          <ol key={`l${key++}`} className={cls}>
            {children}
          </ol>
        ),
      );
      continue;
    }

    // Абзац: все строки подряд до пустой (перенос строки внутри абзаца — не разрыв).
    const paragraph: string[] = [];
    while (i < lines.length && lines[i]!.trim() !== "" && !isBlockStart(lines[i]!)) {
      paragraph.push(lines[i]!.trim());
      i += 1;
    }
    blocks.push(
      // Ширину блоков задаёт не разметка, а колонка вокруг тела статьи (HelpArticlePage):
      // абзацы, заголовки и списки ведут себя одинаково и переносятся по её краю.
      //
      // ИСТОРИЯ РЕШЕНИЯ ЖИВЁТ В docs/07 §6а, А НЕ ЗДЕСЬ. Прежний вариант — «текст во всю ширину
      // панели, ограничения меры строки нет» — владелец принимал дважды (последний раз
      // 23.08.2026) и сам отменил 07.09.2026, когда рабочая область выросла до 1600 px и та же
      // строка стала бы ~200 знаков. Комментарий в коде был единственным местом, где это
      // решение было записано, — потому следующий заход на типографику и начинался каждый раз
      // с нуля.
      <p key={`p${key++}`} className="mt-3 text-[14px] leading-[1.6] text-ink">
        {inline(paragraph.join(" "), `p${key}`)}
      </p>,
    );
  }

  return blocks;
}

function isBlockStart(line: string): boolean {
  return (
    line.startsWith("## ") || line.startsWith("### ") || BULLET.test(line) || NUMBERED.test(line)
  );
}
